/**
 * Canonical price/currency formatting utility.
 *
 * Single source of truth for all price display across the app.
 * Consolidates duplicate formatPrice / formatCurrency implementations
 * from offers, financial, referrals, and dashboard features.
 */

/**
 * Format a price value as a currency string using Intl.NumberFormat.
 *
 * @param price - The numeric price to format, or null to return '-'
 * @param currency - ISO 4217 currency code (default: 'EUR')
 * @param options - Optional overrides for Intl.NumberFormat options
 * @returns Formatted currency string, e.g. "€1,234.56" or "-" for null
 *
 * @example
 * formatPrice(1234.56)          // "€1,235"
 * formatPrice(1234.56, 'USD')   // "$1,235"
 * formatPrice(null)             // "-"
 * formatPrice(0)                // "€0"
 */
export function formatPrice(
  price: number | null | undefined,
  currency: string | null = 'EUR',
  options?: Partial<Intl.NumberFormatOptions>
): string {
  if (price === null || price === undefined) return '-';

  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency || 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
    ...options,
  });

  return formatter.format(price);
}
